import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Card, CardBody, VStack, HStack, Text, Badge, Divider,
  FormControl, FormLabel, Input, Button, useToast, InputGroup,
  InputRightElement, IconButton, Avatar, Flex, Container,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FiUser, FiMail, FiShield, FiLock } from 'react-icons/fi';
import { useUser } from '../Context/UserContext';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

const ProfilePage = () => {
  const { user } = useUser();
  const toast = useToast();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('userToken_TOVIAPPS');
        const res = await fetch(`${API_BASE_URL}api/webuser/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Falha ao buscar perfil');
        const data = await res.json();
        setProfile(data);
      } catch (err) {
        toast({ title: 'Erro', description: err.message, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [toast]);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      return toast({ title: 'Preencha todos os campos', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
    }
    if (newPassword.length < 6) {
      return toast({ title: 'A nova senha deve ter pelo menos 6 caracteres', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
    }
    if (newPassword !== confirmPassword) {
      return toast({ title: 'As senhas não coincidem', status: 'warning', duration: 3000, isClosable: true, position: 'top-right' });
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const res = await fetch(`${API_BASE_URL}api/webuser/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Erro ao alterar senha');
      toast({ title: 'Senha alterada com sucesso', status: 'success', duration: 3000, isClosable: true, position: 'top-right' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast({ title: 'Erro', description: err.message, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    } finally {
      setChangingPassword(false);
    }
  };

  const permissionColors = {
    Master: 'red',
    'Administração': 'purple',
    Suporte: 'blue',
    Financeiro: 'green',
    Comercial: 'orange',
    Marketing: 'pink',
    Design: 'cyan',
    TI: 'teal',
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={8}>
        <Text>Carregando...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.md" py={8}>
      <Heading size="lg" mb={6}>Meu Perfil</Heading>

      {/* Profile Info */}
      <Card mb={6}>
        <CardBody>
          <Flex align="center" gap={5} mb={6}>
            <Avatar size="xl" name={profile?.username || user?.username} bg="teal.500" color="white" />
            <VStack align="start" spacing={1}>
              <Heading size="md">{profile?.username || user?.username}</Heading>
              <Text color="gray.500">{profile?.email || user?.email}</Text>
            </VStack>
          </Flex>

          <Divider mb={4} />

          <VStack align="stretch" spacing={4}>
            <HStack>
              <Box as={FiUser} color="gray.500" />
              <Text fontWeight="medium" minW="100px">Nome:</Text>
              <Text>{profile?.username}</Text>
            </HStack>
            <HStack>
              <Box as={FiMail} color="gray.500" />
              <Text fontWeight="medium" minW="100px">Email:</Text>
              <Text>{profile?.email}</Text>
            </HStack>
            <HStack>
              <Box as={FiShield} color="gray.500" />
              <Text fontWeight="medium" minW="100px">Permissões:</Text>
              <HStack wrap="wrap">
                {(profile?.permissions || user?.permissions || []).map((perm) => (
                  <Badge key={perm} colorScheme={permissionColors[perm] || 'gray'} fontSize="sm" px={2} py={0.5} borderRadius="md">
                    {perm}
                  </Badge>
                ))}
              </HStack>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Change Password */}
      <Card>
        <CardBody>
          <HStack mb={4}>
            <Box as={FiLock} color="gray.500" />
            <Heading size="md">Alterar Senha</Heading>
          </HStack>

          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Senha Atual</FormLabel>
              <InputGroup>
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Digite sua senha atual"
                />
                <InputRightElement>
                  <IconButton size="sm" variant="ghost" icon={showCurrent ? <ViewOffIcon /> : <ViewIcon />} onClick={() => setShowCurrent(!showCurrent)} />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Nova Senha</FormLabel>
              <InputGroup>
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Digite a nova senha (mín. 6 caracteres)"
                />
                <InputRightElement>
                  <IconButton size="sm" variant="ghost" icon={showNew ? <ViewOffIcon /> : <ViewIcon />} onClick={() => setShowNew(!showNew)} />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Confirmar Nova Senha</FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme a nova senha"
              />
            </FormControl>

            <Button
              colorScheme="teal"
              onClick={handleChangePassword}
              isLoading={changingPassword}
              loadingText="Alterando..."
              alignSelf="flex-end"
            >
              Alterar Senha
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
};

export default ProfilePage;
